import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AvailabilityCalendar = ({ vendor, onDateSelect, onCheckAvailability }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return vendor.availability.availableDates.includes(dateStr);
  };

  const isDateBooked = (date) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return vendor.availability.bookedDates.includes(dateStr);
  };

  const isWeekend = (date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateClick = (date) => {
    if (!date || isPastDate(date) || isDateBooked(date)) return;
    
    setSelectedDate(date);
    onDateSelect?.(date);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleCheckAvailability = () => {
    onCheckAvailability?.(selectedDate);
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="bg-white rounded-xl border border-border shadow-soft">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Availability Calendar
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevMonth}
              className="w-8 h-8 rounded-lg hover:bg-surface-100 flex items-center justify-center transition-smooth"
            >
              <Icon name="ChevronLeft" size={16} color="#6B5B4F" />
            </button>
            <span className="font-heading font-semibold text-lg text-text-primary min-w-32 text-center">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>
            <button
              onClick={handleNextMonth}
              className="w-8 h-8 rounded-lg hover:bg-surface-100 flex items-center justify-center transition-smooth"
            >
              <Icon name="ChevronRight" size={16} color="#6B5B4F" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {/* Day Headers */}
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="h-10 flex items-center justify-center text-sm font-medium text-text-secondary"
            >
              {day}
            </div>
          ))}
          
          {/* Calendar Days */}
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="h-10" />;
            }

            const isAvailable = isDateAvailable(date);
            const isBooked = isDateBooked(date);
            const isPast = isPastDate(date);
            const isWeekendDay = isWeekend(date);
            const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

            let cellClass = "h-10 flex items-center justify-center text-sm rounded-lg cursor-pointer transition-smooth ";
            
            if (isPast) {
              cellClass += "text-text-muted cursor-not-allowed ";
            } else if (isBooked) {
              cellClass += "bg-error-50 text-error cursor-not-allowed ";
            } else if (isAvailable) {
              cellClass += isSelected 
                ? "bg-primary text-primary-foreground " 
                : "bg-success-50 text-success hover:bg-success-100 ";
            } else {
              cellClass += "text-text-muted hover:bg-surface-100 ";
            }

            if (isWeekendDay && !isPast && !isBooked) {
              cellClass += "font-semibold ";
            }

            return (
              <button
                key={index}
                onClick={() => handleDateClick(date)}
                className={cellClass}
                disabled={isPast || isBooked}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-success-50 border border-success-200 rounded" />
            <span className="text-text-secondary font-caption">Available</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-error-50 border border-error-200 rounded" />
            <span className="text-text-secondary font-caption">Booked</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-surface-100 border border-border rounded" />
            <span className="text-text-secondary font-caption">Unavailable</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Star" size={16} color="#DAA520" />
            <span className="text-text-secondary font-caption">Weekend dates highlighted</span>
          </div>
        </div>

        {/* Selected Date Info */}
        {selectedDate && (
          <div className="bg-primary-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-primary">
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
                <div className="text-sm text-text-secondary font-caption mt-1">
                  {isWeekend(selectedDate) ? 'Weekend date' : 'Weekday date'} • Available for booking
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                iconName="Calendar"
                iconSize={16}
                onClick={handleCheckAvailability}
              >
                Check Availability
              </Button>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="md"
            iconName="MessageCircle"
            iconSize={16}
            fullWidth
          >
            Message About Dates
          </Button>
          <Button
            variant="secondary"
            size="md"
            iconName="Calendar"
            iconSize={16}
            fullWidth
          >
            Request Custom Date
          </Button>
        </div>

        {/* Availability Notes */}
        <div className="mt-4 p-3 bg-surface-50 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} color="#6B5B4F" className="mt-0.5" />
            <div className="text-sm text-text-secondary font-caption">
              <div className="font-medium text-text-primary mb-1">Booking Notes:</div>
              <ul className="space-y-1">
                <li>• Weekend dates require 2-month advance booking</li>
                <li>• Travel dates may have additional charges</li>
                <li>• Availability subject to confirmation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;